import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Janela "Filtrar faturas" da lista de faturas. Os filtros valem na hora em que
// mudam (o pai guarda o estado); a janela só edita e fecha. No celular abre como
// folha de baixo pra cima; no desktop, centralizada.
// ─────────────────────────────────────────────────────────────────────────────

/** Campos do estado de filtros do FinanceiroLayout que esta janela edita. */
export interface FiltrosDaJanela {
  nome_cliente: string;
  status_fatura: string;
  etiqueta: string;
  usina: string;
  classificacao: string;
  tipo_ligacao: string;
  mes_referencia: string;
  created_at_start: string;
  created_at_end: string;
  data_vencimento_start: string;
  data_vencimento_end: string;
  vencimento_boleto_start: string;
  vencimento_boleto_end: string;
  data_pgto_boleto_start: string;
  data_pgto_boleto_end: string;
  valor_min: string;
  valor_max: string;
  consumo_min: string;
  consumo_max: string;
  saldo_min: string;
  saldo_max: string;
}

export interface OpcaoFiltro {
  value: string;
  label: string;
  /** Texto curto à direita da opção (contagem, descrição). */
  meta?: string;
  /** Classe de fundo de uma bolinha de cor antes do texto (cor da etiqueta). */
  ponto?: string;
}

const STATUS: string[] = [
  'Fatura disponível',
  'Fatura validada',
  'Com boleto calculado',
  'Com boleto emitido / A enviar',
  'Cobrança enviada',
];

const CLASSIFICACOES: OpcaoFiltro[] = [
  { value: 'A3', label: 'A3', meta: 'Industrial' },
  { value: 'A4', label: 'A4', meta: 'Comercial' },
  { value: 'A OPT B3', label: 'A OPT B3', meta: 'Comercial' },
  { value: 'B1', label: 'B1', meta: 'Residencial' },
  { value: 'B2', label: 'B2', meta: 'Rural' },
  { value: 'B3', label: 'B3', meta: 'Industrial/Comercial' },
];

const LIGACOES: OpcaoFiltro[] = ['Monofásico', 'Bifásico', 'Trifásico'].map(l => ({ value: l, label: l }));

const comTodas = (rotulo: string, opcoes: OpcaoFiltro[]): OpcaoFiltro[] => [{ value: '', label: rotulo }, ...opcoes];

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Animações e detalhes que o Tailwind do CDN não cobre (spinner do número, ícone da data).
const ESTILOS = `
.filtro-veu { animation: filtroSurge 160ms ease-out; }
.filtro-painel { animation: filtroFolha 360ms cubic-bezier(0.32, 0.72, 0, 1); }
@media (min-width: 640px) { .filtro-painel { animation: filtroJanela 220ms cubic-bezier(0.23, 1, 0.32, 1); } }
.filtro-lista { animation: filtroLista 140ms cubic-bezier(0.23, 1, 0.32, 1); transform-origin: top; }
.filtro-lista[data-lado="cima"] { animation-name: filtroListaCima; transform-origin: bottom; }
@media (prefers-reduced-motion: reduce) {
  .filtro-painel, .filtro-lista, .filtro-lista[data-lado="cima"] { animation: filtroSurge 160ms ease-out; }
}
@keyframes filtroSurge { from { opacity: 0; } }
@keyframes filtroFolha { from { transform: translateY(100%); } }
@keyframes filtroJanela { from { opacity: 0; transform: translateY(8px) scale(0.97); } }
@keyframes filtroLista { from { opacity: 0; transform: translateY(-4px) scale(0.98); } }
@keyframes filtroListaCima { from { opacity: 0; transform: translateY(4px) scale(0.98); } }
.filtro-num { -moz-appearance: textfield; }
.filtro-num::-webkit-inner-spin-button, .filtro-num::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.filtro-data::-webkit-calendar-picker-indicator { opacity: 0.5; cursor: pointer; transition: opacity 150ms ease; }
.filtro-data:hover::-webkit-calendar-picker-indicator { opacity: 0.9; }
.dark .filtro-data { color-scheme: dark; }
`;

const CONTROLE = 'h-10 w-full min-w-0 rounded-lg border text-sm caret-primary dark:caret-primary-light outline-none transition-[border-color,box-shadow,background-color] duration-150 focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15 dark:focus-visible:border-primary-light dark:focus-visible:ring-primary-light/20';
/** Campo vazio x campo com filtro: o preenchido ganha o tom da marca, pra ver de relance o que está filtrando. */
const VAZIO = 'border-gray-300 bg-white hover:border-gray-400 dark:border-gray-600 dark:bg-gray-900/40 dark:hover:border-gray-500';
const PREENCHIDO = 'border-primary/60 bg-primary/[0.07] dark:border-primary-light/50 dark:bg-primary-light/10';
const TEXTO = 'text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400';
const ROTULO = 'text-[13px] font-medium text-gray-600 dark:text-gray-300';

const tom = (preenchido: boolean) => (preenchido ? PREENCHIDO : VAZIO);

// ─── Select próprio ──────────────────────────────────────────────────────────
// O <select> nativo abre a lista do sistema operacional (azul do Windows) e não
// mostra contagem nem cor. Este segue o padrão combobox do ARIA: foco fica no
// botão, setas navegam, Enter escolhe, Esc fecha, letra pula para a opção.

const SelectFiltro: React.FC<{
  id: string;
  value: string;
  onChange: (v: string) => void;
  opcoes: OpcaoFiltro[];
}> = ({ id, value, onChange, opcoes }) => {
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number; cima: boolean } | null>(null);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const listaRef = useRef<HTMLUListElement>(null);
  const digitado = useRef({ texto: '', em: 0 });
  const selecionada = opcoes.find(o => o.value === value) ?? opcoes[0];

  const abrir = () => {
    const r = gatilhoRef.current?.getBoundingClientRect();
    if (!r) return;
    // Abre pra cima só quando não cabe embaixo e há mais espaço em cima.
    const altura = Math.min(opcoes.length * 36 + 10, 290);
    const espacoAbaixo = window.innerHeight - r.bottom;
    const cima = espacoAbaixo < altura + 12 && r.top > espacoAbaixo;
    setPos({ left: r.left, width: r.width, cima, ...(cima ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }) });
    setAtivo(Math.max(0, opcoes.findIndex(o => o.value === value)));
    setAberto(true);
  };
  const escolher = (v: string) => { onChange(v); setAberto(false); };

  useEffect(() => {
    if (!aberto) return;
    const cliqueFora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (gatilhoRef.current?.contains(alvo) || listaRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    // A lista é fixa na tela: se o corpo da janela rolar, ela se soltaria do campo.
    const rolou = (e: Event) => { if (!listaRef.current?.contains(e.target as Node)) setAberto(false); };
    const fechar = () => setAberto(false);
    document.addEventListener('mousedown', cliqueFora);
    window.addEventListener('scroll', rolou, { capture: true, passive: true });
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      window.removeEventListener('scroll', rolou, { capture: true });
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  useEffect(() => {
    if (aberto) listaRef.current?.querySelector<HTMLElement>(`[data-i="${ativo}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [aberto, ativo]);

  const teclar = (e: React.KeyboardEvent) => {
    if (!aberto) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); abrir(); }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setAtivo(i => Math.min(i + 1, opcoes.length - 1)); break;
      case 'ArrowUp': e.preventDefault(); setAtivo(i => Math.max(i - 1, 0)); break;
      case 'Home': e.preventDefault(); setAtivo(0); break;
      case 'End': e.preventDefault(); setAtivo(opcoes.length - 1); break;
      case 'Enter': case ' ': e.preventDefault(); escolher(opcoes[ativo].value); break;
      // Não deixa o Esc chegar na janela: fecha só a lista.
      case 'Escape': e.preventDefault(); e.stopPropagation(); setAberto(false); break;
      case 'Tab': setAberto(false); break;
      default:
        if (e.key.length === 1) {
          const agora = Date.now();
          digitado.current = { texto: (agora - digitado.current.em > 700 ? '' : digitado.current.texto) + semAcento(e.key), em: agora };
          const i = opcoes.findIndex(o => semAcento(o.label).startsWith(digitado.current.texto));
          if (i >= 0) setAtivo(i);
        }
    }
  };

  return (
    <>
      <button
        ref={gatilhoRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-controls={`${id}-lista`}
        aria-labelledby={`${id}-rotulo ${id}`}
        aria-activedescendant={aberto ? `${id}-op-${ativo}` : undefined}
        onClick={() => (aberto ? setAberto(false) : abrir())}
        onKeyDown={teclar}
        onKeyUp={e => { if (e.key === ' ') e.preventDefault(); }}
        className={`${CONTROLE} ${tom(!!value)} ${aberto ? 'border-primary ring-4 ring-primary/15 dark:border-primary-light dark:ring-primary-light/20' : ''} flex items-center gap-2 px-3 text-left`}
      >
        {selecionada.ponto && <span className={`size-2 shrink-0 rounded-full ${selecionada.ponto}`} />}
        <span className={`truncate ${value ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'}`}>{selecionada.label}</span>
        {value && selecionada.meta && (
          <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">{selecionada.meta}</span>
        )}
        <ChevronDown
          size={16}
          className={`ml-auto shrink-0 text-gray-400 transition-transform duration-200 ease-out ${aberto ? 'rotate-180' : ''}`}
        />
      </button>

      {aberto && pos && createPortal(
        <ul
          ref={listaRef}
          id={`${id}-lista`}
          role="listbox"
          aria-labelledby={`${id}-rotulo`}
          data-lado={pos.cima ? 'cima' : 'baixo'}
          // Clicar na lista não pode tirar o foco do botão (setas e Esc seguem valendo).
          onMouseDown={e => e.preventDefault()}
          style={{ position: 'fixed', left: pos.left, width: pos.width, ...(pos.cima ? { bottom: pos.bottom } : { top: pos.top }) }}
          className="filtro-lista z-[10050] max-h-72 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white p-1 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.22),0_2px_6px_-2px_rgba(15,23,42,0.08)] dark:border-gray-700 dark:bg-gray-800"
        >
          {opcoes.map((o, i) => {
            const marcada = o.value === value;
            return (
              <li
                key={o.value || '__todas__'}
                id={`${id}-op-${i}`}
                data-i={i}
                role="option"
                aria-selected={marcada}
                onMouseEnter={() => setAtivo(i)}
                onClick={() => escolher(o.value)}
                className={`flex h-9 cursor-pointer items-center gap-2 rounded-lg px-2.5 text-sm select-none ${
                  i === ativo ? 'bg-gray-100 dark:bg-gray-700/70' : ''
                } ${marcada ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}`}
              >
                {o.ponto && <span className={`size-2 shrink-0 rounded-full ${o.ponto}`} />}
                <span className="truncate">{o.label}</span>
                {o.meta && <span className="ml-auto shrink-0 pl-2 text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">{o.meta}</span>}
                <Check
                  size={15}
                  strokeWidth={2.5}
                  className={`shrink-0 text-primary dark:text-primary-light ${o.meta ? '' : 'ml-auto'} ${marcada ? '' : 'invisible'}`}
                />
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </>
  );
};

// ─── Peças do formulário ─────────────────────────────────────────────────────

const Campo: React.FC<{ id: string; rotulo: string; children: React.ReactNode }> = ({ id, rotulo, children }) => (
  <div className="min-w-0">
    <label id={`${id}-rotulo`} htmlFor={id} className={`mb-1.5 block ${ROTULO}`}>{rotulo}</label>
    {children}
  </div>
);

/** Uma linha "rótulo | de até": datas e valores usam a mesma grade, então tudo alinha. */
const LinhaFaixa: React.FC<{ id: string; rotulo: string; de: React.ReactNode; ate: React.ReactNode }> = ({ id, rotulo, de, ate }) => (
  <div role="group" aria-labelledby={`${id}-rotulo`} className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[10.5rem_minmax(0,1fr)] sm:items-center">
    <span id={`${id}-rotulo`} className={ROTULO}>{rotulo}</span>
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
      {de}
      <span className="text-xs text-gray-500 dark:text-gray-400">até</span>
      {ate}
    </div>
  </div>
);

const CampoData: React.FC<{ rotulo: string; value: string; onChange: (v: string) => void }> = ({ rotulo, value, onChange }) => (
  <input
    type="date"
    aria-label={rotulo}
    value={value}
    onChange={e => onChange(e.target.value)}
    className={`filtro-data ${CONTROLE} ${tom(!!value)} px-2.5 tabular-nums sm:px-3 ${value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}
  />
);

const CampoNumero: React.FC<{
  rotulo: string; placeholder: string; value: string; onChange: (v: string) => void; prefixo?: string; sufixo?: string;
}> = ({ rotulo, placeholder, value, onChange, prefixo, sufixo }) => (
  <div className="relative min-w-0">
    {prefixo && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">{prefixo}</span>}
    <input
      type="number"
      inputMode="decimal"
      aria-label={rotulo}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`filtro-num ${CONTROLE} ${tom(!!value)} ${TEXTO} tabular-nums ${prefixo ? 'pl-9' : 'pl-3'} ${sufixo ? 'pr-11' : 'pr-3'}`}
    />
    {sufixo && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">{sufixo}</span>}
  </div>
);

const Secao: React.FC<{ id: string; titulo: string; children: React.ReactNode }> = ({ id, titulo, children }) => (
  <section aria-labelledby={id} className="space-y-3 border-t border-gray-100 pt-5 dark:border-gray-700/70">
    <h4 id={id} className="text-sm font-semibold text-gray-900 dark:text-gray-100">{titulo}</h4>
    {children}
  </section>
);

// ─── Janela ──────────────────────────────────────────────────────────────────

const FiltroFaturasModal: React.FC<{
  onFechar: () => void;
  filtros: FiltrosDaJanela;
  onMudar: (campo: keyof FiltrosDaJanela, valor: string) => void;
  /** Quantas faturas há em cada status (vai ao lado da opção). */
  contagemStatus: Record<string, number>;
  /** Sem a lista (sem permissão ou sem etiqueta criada), o campo some. */
  opcoesEtiqueta?: OpcaoFiltro[];
  opcoesUsina?: OpcaoFiltro[];
  /** Conta também os filtros que ficam fora da janela (atalhos da barra). */
  filtrosAtivos: number;
  onLimpar: () => void;
}> = ({ onFechar, filtros, onMudar, contagemStatus, opcoesEtiqueta, opcoesUsina, filtrosAtivos, onLimpar }) => {
  const painelRef = useRef<HTMLDivElement>(null);

  // Foco vai pra janela (o Esc passa a valer) e volta pro botão "Filtros" ao fechar.
  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    painelRef.current?.focus({ preventScroll: true });
    return () => anterior?.focus?.({ preventScroll: true });
  }, []);

  const campo = (k: keyof FiltrosDaJanela) => ({ value: filtros[k], onChange: (v: string) => onMudar(k, v) });

  const opcoesStatus = comTodas('Todos', STATUS.map(s => ({
    value: s, label: s, meta: (contagemStatus[s] ?? 0).toLocaleString('pt-BR'),
  })));

  return createPortal(
    <div
      className="filtro-veu fixed inset-0 z-50 flex items-end justify-center bg-gray-950/50 backdrop-blur-[2px] sm:items-center sm:p-4"
      onClick={e => { if (e.target === e.currentTarget) onFechar(); }}
    >
      <style>{ESTILOS}</style>
      <div
        ref={painelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="filtro-faturas-titulo"
        tabIndex={-1}
        onKeyDown={e => { if (e.key === 'Escape') onFechar(); }}
        className="filtro-painel flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-[0_24px_64px_-16px_rgba(15,23,42,0.4)] outline-none ring-1 ring-black/5 sm:max-h-[min(88vh,820px)] sm:max-w-2xl sm:rounded-2xl dark:bg-gray-800 dark:ring-white/10"
      >
        {/* Cabeçalho */}
        <div className="flex shrink-0 items-center gap-3 border-b border-gray-100 px-5 py-4 sm:px-6 dark:border-gray-700/70">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light">
            <SlidersHorizontal size={18} />
          </div>
          <h3 id="filtro-faturas-titulo" className="text-lg font-semibold text-gray-900 dark:text-white">Filtrar faturas</h3>
          {filtrosAtivos > 0 && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary dark:bg-primary-light/15 dark:text-primary-light">
              {filtrosAtivos} {filtrosAtivos === 1 ? 'ativo' : 'ativos'}
            </span>
          )}
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            className="ml-auto flex size-9 items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        {/* Corpo — só ele rola; cabeçalho e rodapé ficam parados */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          <div className="space-y-4">
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                aria-label="Nome do cliente"
                placeholder="Buscar por nome do cliente"
                value={filtros.nome_cliente}
                onChange={e => onMudar('nome_cliente', e.target.value)}
                className={`${CONTROLE} ${tom(!!filtros.nome_cliente)} ${TEXTO} pl-9 pr-9`}
              />
              {filtros.nome_cliente && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  onClick={() => onMudar('nome_cliente', '')}
                  className="absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <Campo id="filtro-status" rotulo="Status da fatura">
                <SelectFiltro id="filtro-status" opcoes={opcoesStatus} {...campo('status_fatura')} />
              </Campo>
              {opcoesEtiqueta && (
                <Campo id="filtro-etiqueta" rotulo="Etiqueta da UC">
                  <SelectFiltro id="filtro-etiqueta" opcoes={comTodas('Todas', opcoesEtiqueta)} {...campo('etiqueta')} />
                </Campo>
              )}
              {opcoesUsina && (
                <Campo id="filtro-usina" rotulo="Usina">
                  <SelectFiltro id="filtro-usina" opcoes={comTodas('Todas', opcoesUsina)} {...campo('usina')} />
                </Campo>
              )}
              <Campo id="filtro-classificacao" rotulo="Classificação">
                <SelectFiltro id="filtro-classificacao" opcoes={comTodas('Todas', CLASSIFICACOES)} {...campo('classificacao')} />
              </Campo>
              <Campo id="filtro-ligacao" rotulo="Tipo de ligação">
                <SelectFiltro id="filtro-ligacao" opcoes={comTodas('Todos', LIGACOES)} {...campo('tipo_ligacao')} />
              </Campo>
              <Campo id="filtro-mes" rotulo="Mês de referência">
                <input
                  id="filtro-mes"
                  type="text"
                  placeholder="ex.: FEV/2026"
                  value={filtros.mes_referencia}
                  onChange={e => onMudar('mes_referencia', e.target.value)}
                  className={`${CONTROLE} ${tom(!!filtros.mes_referencia)} ${TEXTO} px-3`}
                />
              </Campo>
            </div>
          </div>

          <Secao id="filtro-sec-datas" titulo="Datas">
            <LinhaFaixa
              id="filtro-criacao" rotulo="Criação"
              de={<CampoData rotulo="Criação a partir de" {...campo('created_at_start')} />}
              ate={<CampoData rotulo="Criação até" {...campo('created_at_end')} />}
            />
            <LinhaFaixa
              id="filtro-vencimento" rotulo="Vencimento da fatura"
              de={<CampoData rotulo="Vencimento da fatura a partir de" {...campo('data_vencimento_start')} />}
              ate={<CampoData rotulo="Vencimento da fatura até" {...campo('data_vencimento_end')} />}
            />
            <LinhaFaixa
              id="filtro-venc-boleto" rotulo="Vencimento do boleto"
              de={<CampoData rotulo="Vencimento do boleto a partir de" {...campo('vencimento_boleto_start')} />}
              ate={<CampoData rotulo="Vencimento do boleto até" {...campo('vencimento_boleto_end')} />}
            />
            <LinhaFaixa
              id="filtro-pagamento" rotulo="Pagamento"
              de={<CampoData rotulo="Pagamento a partir de" {...campo('data_pgto_boleto_start')} />}
              ate={<CampoData rotulo="Pagamento até" {...campo('data_pgto_boleto_end')} />}
            />
          </Secao>

          <Secao id="filtro-sec-valores" titulo="Valores">
            <LinhaFaixa
              id="filtro-valor" rotulo="Valor da fatura"
              de={<CampoNumero rotulo="Valor mínimo" placeholder="Mín" prefixo="R$" {...campo('valor_min')} />}
              ate={<CampoNumero rotulo="Valor máximo" placeholder="Máx" prefixo="R$" {...campo('valor_max')} />}
            />
            <LinhaFaixa
              id="filtro-consumo" rotulo="Consumo"
              de={<CampoNumero rotulo="Consumo mínimo" placeholder="Mín" sufixo="kWh" {...campo('consumo_min')} />}
              ate={<CampoNumero rotulo="Consumo máximo" placeholder="Máx" sufixo="kWh" {...campo('consumo_max')} />}
            />
            <LinhaFaixa
              id="filtro-saldo" rotulo="Saldo acumulado"
              de={<CampoNumero rotulo="Saldo mínimo" placeholder="Mín" sufixo="kWh" {...campo('saldo_min')} />}
              ate={<CampoNumero rotulo="Saldo máximo" placeholder="Máx" sufixo="kWh" {...campo('saldo_max')} />}
            />
          </Secao>
        </div>

        {/* Rodapé */}
        <div className="flex shrink-0 items-center gap-3 border-t border-gray-100 bg-gray-50/80 px-5 pb-[max(0.875rem,env(safe-area-inset-bottom))] pt-3.5 sm:px-6 dark:border-gray-700/70 dark:bg-gray-900/30">
          <button
            type="button"
            onClick={onLimpar}
            disabled={filtrosAtivos === 0}
            className="h-10 rounded-lg px-3 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-200/70 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
          >
            Limpar filtros
          </button>
          <button
            type="button"
            onClick={onFechar}
            className="ml-auto h-10 flex-1 rounded-lg bg-primary px-6 text-sm font-semibold text-white shadow-[0_1px_2px_rgba(33,41,19,0.25)] transition-[transform,background-color] duration-150 ease-out hover:bg-[#535f30] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.97] sm:flex-none dark:focus-visible:ring-offset-gray-800"
          >
            Ver faturas
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FiltroFaturasModal;
